import { Link } from "wouter";
import { Accessibility, ArrowLeft, Heart } from "lucide-react";
import { LegalFooter } from "@/components/layout/legal-footer";

export default function AccessibilityStatement() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500" />
            <span className="font-bold text-slate-800">RheumHub</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        <div className="flex items-start gap-4">
          <div className="bg-teal-50 p-3 rounded-2xl">
            <Accessibility className="h-8 w-8 text-teal-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Accessibility Statement</h1>
            <p className="text-slate-500 mt-1">Last reviewed: May 2026 — RheumHub web application</p>
          </div>
        </div>

        <div className="space-y-6">

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Our commitment</h2>
            <p className="text-slate-700 leading-relaxed">
              <strong>Medantix Health Ltd</strong> is committed to making the RheumHub website accessible in accordance with the <strong>Public Sector Bodies (Websites and Mobile Applications) Accessibility Regulations 2018</strong> and the <strong>Equality Act 2010</strong>. We aim to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong>.
            </p>
            <p className="text-slate-700 leading-relaxed">
              This statement applies to the RheumHub web application hosted at <strong>rheumhub.co.uk</strong> and its subdomains.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">What we do well</h2>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Keyboard navigation is supported for all primary workflows (sign-in, role selection, navigation, forms, AI chat).</li>
              <li>Colour contrast on primary actions and body text meets or exceeds WCAG AA ratio (4.5:1 for normal text, 3:1 for large text).</li>
              <li>All images and icons that convey information include descriptive <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">alt</code> text or <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-label</code> attributes.</li>
              <li>Form fields are associated with visible labels.</li>
              <li>Heading hierarchy follows a logical structure to assist screen reader navigation.</li>
              <li>Error messages are associated with the fields they describe.</li>
              <li>The platform supports browser zoom up to 200% without loss of content or functionality on primary pages.</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Known limitations</h2>
            <p className="text-slate-700">We are aware of the following accessibility limitations that we are actively working to address:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>PDF and external documents</strong>: Links to external PDFs (e.g. NICE guidelines, BSR guidelines) are not produced or controlled by RheumHub and their accessibility cannot be guaranteed.</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Steps we are taking</h2>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>An independent WCAG 2.1 AA accessibility audit is planned prior to full public launch.</li>
              <li>All new features are reviewed for accessibility compliance before release.</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-emerald-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Recently resolved</h2>
            <p className="text-slate-700">The following issues were identified and remediated ahead of the August 2026 pilot:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Drug monitoring data tables</strong>: All header cells in the DMARD and biologic monitoring tables now include <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">scope="col"</code> attributes to support screen reader table navigation.</li>
              <li><strong>Accordion expand/collapse controls</strong>: Expand/collapse buttons across the drug monitoring, prescribing, exercise protocol, and condition protocol pages now expose their open/closed state via <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-expanded</code>, with <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-controls</code> linking each button to its associated panel where applicable.</li>
              <li><strong>Custom select (dropdown) triggers</strong>: The shared <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">SelectTrigger</code> component now explicitly declares <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-haspopup="listbox"</code>, ensuring assistive technologies correctly identify it as a listbox control. Open/closed state is conveyed via <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-expanded</code>, managed by the underlying Radix UI primitive.</li>
              <li><strong>Disease activity calculator tabs</strong>: The calculator selector now uses <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">role="tablist"</code>, <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">role="tab"</code>, and <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-selected</code> so assistive technologies correctly identify the active calculator.</li>
              <li><strong>AI chat streaming responses</strong>: The conversation message area in both the nurse and patient AI chat now carries <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">aria-live="polite"</code> and <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">role="log"</code>, allowing screen readers to announce new messages as they arrive.</li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Requesting accessible content or an adjustment</h2>
            <p className="text-slate-700 leading-relaxed">
              If you need information from RheumHub in a different format (such as plain text, large print, or audio), or if you have difficulty accessing any part of the platform, please contact us:
            </p>
            <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 text-teal-800 text-sm">
              <p><strong>Accessibility requests:</strong> <a href="mailto:accessibility@medantixhealth.co.uk" className="underline hover:text-teal-900">accessibility@medantixhealth.co.uk</a></p>
              <p className="mt-1">We aim to respond to accessibility queries within <strong>5 working days</strong>.</p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Enforcement and escalation</h2>
            <p className="text-slate-700 leading-relaxed">
              If you have made an accessibility request and are not satisfied with our response, you can contact the <strong>Equality Advisory and Support Service (EASS)</strong>:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
              <li>Website: <a href="https://www.equalityadvisoryservice.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-900">equalityadvisoryservice.com</a></li>
              <li>Phone: 0808 800 0082 (free, Monday–Friday 09:00–20:00, Saturday 10:00–14:00)</li>
              <li>Textphone: 0808 800 0084</li>
            </ul>
            <p className="text-slate-700 leading-relaxed text-sm">
              The EASS is not a regulator but can provide advice and assistance. For public sector body complaints, the relevant enforcement body is the <strong>Equality and Human Rights Commission (EHRC)</strong>.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Technical information</h2>
            <p className="text-slate-700 leading-relaxed">
              RheumHub is built using React, TypeScript, and Tailwind CSS. The platform uses semantic HTML5 elements and ARIA where appropriate. It is tested primarily in the latest versions of Chrome, Firefox, Safari, and Edge on desktop.
            </p>
            <p className="text-slate-700 leading-relaxed">
              This statement was prepared on <strong>1 May 2026</strong>. It was last reviewed on <strong>14 May 2026</strong>.
            </p>
          </section>

        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
